import React, { useState } from 'react'
import AdditionalFields from './AdditionalFields';
import { getCaseType, getNetWorth } from 'Utils';
import { formElements } from '../CaseFormFields';
import { FileText } from 'lucide-react';
import CaseDetailsToggleTile from './CaseDetailsToggleTile';

function Row({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className='flex justify-between gap-4 py-2.5 border-b border-[rgba(20,33,61,.08)] last:border-b-0'>
      <div className='text-[13px] text-[rgba(20,33,61,.55)]'>{label}</div>
      <div className='text-[13px] font-medium text-navy text-right'>{value}</div>
    </div>
  );
}

function CaseDetailsInformation({
    caseDetails,
    additionalFields,
    setAdditionalFields,
    setUpdateComp,
    uploadDocuments,
    removeDoc,
    isCaseOpen,
    id,
    setShowModal
}) {
    const [toggleInfo, setToggleInfo] = useState(false);

    const yesNo = (val) => val === true || val === "yes" ? "Yes" : (val === false || val === "no" ? "No" : undefined);

    // The severity wizard (CaseFormFields.js, formElements step 4) is the
    // single source of truth for these fields' real question wording and
    // option labels - reading from it here (instead of a separately
    // maintained copy) keeps this panel showing the same words the client
    // was actually asked, and the same real answers, rather than the old
    // 1-10 injuriesRate/economicDamagesRate/nonEconomicDamagesRate/proofRate
    // scores those questions were deprecated in favor of.
    const severityFields = formElements.find((s) => s.step === 4)?.sections?.flatMap((sec) => sec.fields) || [];
    const fieldByName = (name) => severityFields.find((f) => f.name === name);
    const optionLabel = (name, value) => fieldByName(name)?.options?.find((o) => o.id === value)?.name;
    const checklistLabels = (name, ids) => {
      if (!Array.isArray(ids) || ids.length === 0) return undefined;
      const field = fieldByName(name);
      return ids.map((id) => field?.options?.find((o) => o.id === id)?.name || id).join(", ");
    };

    const adverseType = caseDetails?.isEntity === "yes"
      ? ("Company" + (caseDetails?.adversePartyPublicCompany === "yes" ? " · Public company" : ""))
      : (caseDetails?.isEntity === "no" ? "Individual" : undefined);

    const cityStateZip = (city, state, zip) => [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ") || undefined;

  return (
    <div className='font-plexsans'>
       <CaseDetailsToggleTile
        toggleInfo={toggleInfo}
        setToggleInfo={setToggleInfo}
        title={"Provided Case Information"}
        icon = {<FileText size={18} />}
        description={"Entered at case creation"}
        />
            {toggleInfo && (
    <div className='w-full grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2'>

       <div className='w-full rounded-[10px] border border-[rgba(20,33,61,.12)] bg-ivory p-4'>
            <Row label="Case category" value={getCaseType(caseDetails?.caseType)} />
            <Row label="City / State / Zip" value={cityStateZip(caseDetails?.clientCity, caseDetails?.clientState, caseDetails?.clientZipcode)} />
            <Row label="Adverse party" value={caseDetails?.adversePartyName} />
            <Row label="Incident date" value={caseDetails?.dateInfo ? new Date(caseDetails.dateInfo).toLocaleDateString() : undefined} />
            <Row label="Incident location" value={cityStateZip(caseDetails?.caseCity, caseDetails?.caseState, caseDetails?.caseZipcode)} />
            <Row label="SOS case" value={yesNo(caseDetails?.sos)} />
            <Row label="Class action" value={yesNo(caseDetails?.isClassAction)} />
            <Row label="Adverse party type" value={adverseType} />
            <Row label="Adverse party net worth" value={getNetWorth(caseDetails?.adversePartyNetWorth)} />
            <Row label="Currently represented" value={yesNo(caseDetails?.attAvailable)} />
       </div>

        <div className='w-full rounded-[10px] border border-[rgba(20,33,61,.12)] bg-ivory p-4'>
            <Row label="Injury severity" value={optionLabel("injuriesSeverity", caseDetails?.injuriesSeverity)} />
            <Row label="Hospitalized" value={optionLabel("wasHospitalized", caseDetails?.wasHospitalized)} />
            <Row label="Required surgery" value={optionLabel("requiredSurgery", caseDetails?.requiredSurgery)} />
            <Row label="Still receiving treatment" value={optionLabel("stillReceivingTreatment", caseDetails?.stillReceivingTreatment)} />
            <Row label="Medical bills" value={caseDetails?.hasMedicalBills === "yes" ? optionLabel("medicalBillsRange", caseDetails?.medicalBillsRange) : optionLabel("hasMedicalBills", caseDetails?.hasMedicalBills)} />
            <Row label="Missed work" value={caseDetails?.hasMissedWork === "yes" ? optionLabel("missedWorkDays", caseDetails?.missedWorkDays) : optionLabel("hasMissedWork", caseDetails?.hasMissedWork)} />
            <Row label="Ongoing medical costs expected" value={optionLabel("expectOngoingMedicalCosts", caseDetails?.expectOngoingMedicalCosts)} />
            <Row label="Property damage" value={optionLabel("hadPropertyDamage", caseDetails?.hadPropertyDamage)} />
            <Row label="Day-to-day impact" value={optionLabel("dayToDayImpact", caseDetails?.dayToDayImpact)} />
            <Row label="Contributing factors" value={checklistLabels("nonEconomicFactors", caseDetails?.nonEconomicFactors)} />
            <Row label="Evidence available" value={checklistLabels("evidenceTypes", caseDetails?.evidenceTypes)} />
            <Row label="Proof details" value={caseDetails?.proofList} />

            {Object.entries(additionalFields || {}).length ? (
                <div className='mt-4 pt-4 border-t border-[rgba(20,33,61,.1)] max-h-[400px] overflow-y-auto'>
                    <AdditionalFields
                        additionalFields={additionalFields}
                    />
                </div>
            ) : ""}

            {/* extra notes can't be added or edited after the case is created (for now) -
                notes a case already has are shown read-only */}
            {caseDetails?.additionalNotes ? (
                <div className='mt-4 pt-4 border-t border-[rgba(20,33,61,.1)]'>
                    <div className='text-[13px] font-semibold text-navy mb-1'>More information on the issue</div>
                    <div className='text-[13px] text-navy whitespace-pre-line'>{caseDetails.additionalNotes}</div>
                </div>
            ) : null}
        </div>
    </div>)}
    </div>
  )
}

export default CaseDetailsInformation
